import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isDoublesDiscipline } from './discipline';

import { apiUrl } from '../utils/apiBase';
import ScoringGroupPicker from './scoring-group-picker';
import ScoringGroupView from './scoring-group-view';
import ScoringRoundStandings from './scoring-round-standings';
import RotationNextRun from './rotation-next-run';
import { useScoringViewMode } from './scoring-view-mode';
import {
  groupKey,
  matchesOf,
  ratingWeightLabel,
  resolveGroups,
  type ScoringGroup,
  type ScoringResponse,
} from './scoring-types';

interface ScoringPageProps {
  token?: string;
  // Framed on a club's site: links out of the round would open inside the frame.
  embedded?: boolean;
  // The token is the read-only view token, not the share token.
  resultsOnly?: boolean;
}

const ScoringPage: React.FC<ScoringPageProps> = (props) => {
  const params = useParams<{ token: string }>();
  const token = props.token ?? params.token;
  const scoringPath = props.resultsOnly ? 'scoring-view' : 'scoring';
  const [data, setData] = useState<ScoringResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [inactive, setInactive] = useState(false);
  const [pickedKey, setPickedKey] = useState<string | null>(null);
  const [viewMode, setViewMode] = useScoringViewMode();

  const fetchScoring = useCallback(async () => {
    if (!token) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(apiUrl(`/api/public/${scoringPath}/${token}`));

      if (response.status === 404) {
        setInactive(true);
        setData(null);

        return;
      }

      if (response.status === 429) {
        setError('Zbyt wiele żądań — odczekaj chwilę i odśwież stronę.');

        return;
      }

      if (!response.ok) {
        setError(`Błąd serwera (${response.status})`);

        return;
      }

      setData(await response.json() as ScoringResponse);
    } catch {
      setError('Błąd połączenia z serwerem.');
    } finally {
      setLoading(false);
    }
  }, [token, scoringPath]);

  useEffect(() => {
    void fetchScoring();
  }, [fetchScoring]);

  const groups = useMemo(() => (data ? resolveGroups(data) : []), [data]);
  const openGroup: ScoringGroup | null =
    groups.length === 1 ? groups[0] : groups.find((group) => groupKey(group) === pickedKey) ?? null;

  if (loading && data === null) {
    return (
      <div data-testid="scoring-loading" className="scoring-loading">
        Ładowanie…
      </div>
    );
  }

  if (inactive) {
    return (
      <div data-testid="scoring-inactive" className="scoring-inactive-page">
        <h1>Link nieaktywny</h1>
        <p>{props.resultsOnly ? 'Ten link do wyników' : 'Ten link do wpisywania wyników'} jest nieaktywny lub wygasł.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="scoring-error" className="scoring-error">
        <h1>Błąd</h1>
        <p>{error}</p>
        <button
          type="button"
          className="btn-secondary"
          data-testid="scoring-retry-btn"
          onClick={() => void fetchScoring()}
        >
          Spróbuj ponownie
        </button>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const isClosed = data.status === 'CLOSED';
  const weightLabel = ratingWeightLabel(data.ratingWeight);
  const doubles = isDoublesDiscipline(data.round.league.discipline);
  const big = viewMode === 'big';

  return (
    <div
      data-testid="scoring-page"
      className={big ? 'scoring-page-container scoring-page-container--big' : 'scoring-page-container'}
    >
      <header className="scoring-head">
        <button
          type="button"
          className="scoring-view-toggle"
          data-testid="scoring-view-toggle"
          aria-pressed={big}
          onClick={() => setViewMode(big ? 'full' : 'big')}
        >
          {big ? 'Pełny widok' : 'Duży widok'}
        </button>
        <h1 data-testid="scoring-title" className="scoring-title">
          {data.round.league.name} · Kolejka {data.round.number}
        </h1>
        <p data-testid="scoring-season" className="scoring-season">
          {data.round.season.name} · {data.round.name}
        </p>
        {!big && !props.embedded && (
          <nav className="scoring-head__links">
            <Link
              to={`/leagues/${data.round.league.id}`}
              className="round-meta__chip round-meta__chip--link"
              data-testid="scoring-league-link"
            >
              🏸 <span className="round-meta__chip--link__text">{data.round.league.name}</span>
            </Link>
            <Link
              to={`/leagues/${data.round.league.id}/standings?seasonId=${data.round.season.id}`}
              className="scoring-season-ranking-link"
              data-testid="scoring-season-link"
              title={data.round.season.name}
            >
              🏆 Ranking aktualnego sezonu
            </Link>
            {weightLabel && (
              <span
                className="round-meta__chip"
                data-testid="scoring-rating-weight"
                title="Pary tasują się co rundę i gra się do jednego seta, więc jeden wynik rusza ranking o połowę tego, co mecz w stałym składzie."
              >
                ⚖️ Ranking {weightLabel}
              </span>
            )}
          </nav>
        )}
      </header>

      {isClosed && !props.resultsOnly && (
        <div
          role="status"
          data-testid="scoring-closed-banner"
          className="scoring-closed-banner round-meta__chip"
        >
          Kolejka zakończona — wyniki są tylko do podglądu.
        </div>
      )}

      {data.rotation && !isClosed && (
        <RotationNextRun
          token={token!}
          rotation={data.rotation}
          onDealt={fetchScoring}
        />
      )}

      {data.matches.length === 0 && (
        <p className="scoring-no-matches">Brak meczów w tej kolejce.</p>
      )}

      {openGroup === null && groups.length > 0 && (
        <>
          <ScoringGroupPicker groups={groups} onPick={(group) => setPickedKey(groupKey(group))} />
          <ScoringRoundStandings
            groups={groups}
            title="Klasyfikacja kolejki"
            doubles={doubles}
            big={big}
            onOpenGroup={(group) => setPickedKey(groupKey(group))}
          />
        </>
      )}

      {openGroup !== null && (
        <>
          <ScoringGroupView
            key={groupKey(openGroup)}
            group={openGroup}
            matches={matchesOf(data.matches, openGroup)}
            token={token!}
            canSubmit={data.canSubmit}
            showBack={groups.length > 1}
            doubles={doubles}
            runUnit={data.playMode === 'AMERICANO' ? 'runda' : 'kółko'}
            big={big}
            onBack={() => setPickedKey(null)}
            onSaved={fetchScoring}
          />
          {!big && (
            <ScoringRoundStandings
              groups={groups}
              title="Pozostałe grupy"
              doubles={doubles}
              skipKey={groupKey(openGroup)}
              onOpenGroup={(group) => setPickedKey(groupKey(group))}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ScoringPage;
